/**
 * FrameCore - 轻量级模板引擎核心类
 *
 * 提供以下功能：
 * - 数据绑定：{this.xxx} 和 {{xxx}} 模板替换
 * - 条件渲染：hz-if 指令
 * - 列表渲染：hz-for 指令
 * - 事件绑定：hz-click 指令
 */

import { IfDirective, ClickDirective, ForDirective } from "./directives";
import { ExpressionParser } from "./expression";
import { TemplateProcessor } from "./template";

import type { InsertPosition, DataValue, DataObject, GetDataFn } from "./types";

// 重新导出类型供外部使用
export type { InsertPosition, DataValue, DataObject } from "./types";

/**
 * FrameCore 核心类
 */
export class FrameCore {
  protected readonly scopeElement: Element;
  protected data: DataObject = {};

  // 模块实例
  private expressionParser = new ExpressionParser();
  private templateProcessor = new TemplateProcessor();
  private ifDirective = new IfDirective();
  private clickDirective = new ClickDirective();
  private forDirective = new ForDirective();

  constructor(scopeElement: Element) {
    this.scopeElement = scopeElement;
  }

  /**
   * 设置数据并触发更新
   */
  setData(data: DataObject): void {
    this.data = { ...this.data, ...data };
    this.updateText();
    this.bindClickEvent(); // Re-bind global clicks? Actually processElement handles this now.
  }

  /**
   * 获取数据或整个 data 对象
   */
  getData(key?: string): DataValue {
    return key != undefined ? this.getDataValue(key) : this.data;
  }

  /**
   * 向 scope 插入 HTML，并绑定事件、更新文本
   */
  insert(str: string, position: InsertPosition = "beforeend"): void {
    this.scopeElement.insertAdjacentHTML(position, str);
    this.updateText();
    // bindClickEvent is handled inside processElement recursively
  }

  /**
   * 主更新入口
   */
  updateText(): void {
    const getGlobalData = this.getDataValue.bind(this);

    // 1. 处理 hz-for 循环 (展开 DOM)
    // 这里的 processNode 会被 callback 调用，处理生成的元素
    this.forDirective.process(
      this.scopeElement,
      getGlobalData,
      (el, itemData) => {
        this.processElement(el, itemData);
      },
    );

    // 2. 检查 hz-if 恢复的元素 (可能在循环之外，或者循环内被隐藏的)
    // 注意：ForDirective 重新生成元素时，IfDirective 状态可能已经丢失(因为是新元素)。
    // 但对于非循环元素，if恢复逻辑仍然有效。
    const restoredNodes = this.ifDirective.restoreHiddenNodes(getGlobalData);
    for (const { element, scopeData } of restoredNodes) {
      this.processElement(element, scopeData);
    }

    // 3. 处理整个树 (跳过已处理的循环元素)
    // 顶层调用时不带 scopeData (使用全局数据)
    this.processElement(this.scopeElement);
  }

  /**
   * 绑定 hz-click 事件 (保留兼容性接口，实际由 processElement 处理)
   */
  bindClickEvent(): void {
    // 实际逻辑已集成在 processElement 中，这里可保留为空或作为手动重新绑定的入口
    // 为兼容旧代码调用，我们可以在这里做一次全量绑定
    // 但由于 processElement 已经处理了绑定，这里再次绑定可能会重复(ClickDirective已处理去重)
    // 但为了确保 context 正确，这里的全量绑定只能使用 全局数据，会丢失 Scope。
    // 所以不建议手动调用此方法用于 Scoped 元素。
    // 仅对非 Scoped 元素有效。
    this.clickDirective.bind(
      this.scopeElement,
      this,
      this.getDataValue.bind(this),
    );
  }

  /**
   * 递归处理节点
   * @param element 当前处理的元素
   * @param scopeData 当前作用域数据 (循环变量等)
   */
  private processElement(element: Element, scopeData?: DataObject): void {
    // 构建数据获取函数 (合并 Scope 和 Global)
    const getData: GetDataFn = (key: string) => {
      // 1. 尝试从 Scope 获取 (支持 item.id 这种路径)
      if (scopeData) {
        // 特殊处理 $scope
        if (key === "$scope") {
          return scopeData;
        }
        if (key.startsWith("$scope.")) {
          return this.expressionParser.getNestedValue(scopeData, key.slice(7));
        }
        if (key in scopeData) {
          return scopeData[key];
        }
        // 嵌套查找 (ExpressionParser logic helper?)
        // 这里可以直接用 expressionParser.getNestedValue
        const val = this.expressionParser.getNestedValue(scopeData, key);
        if (val !== undefined) {
          return val;
        }
      }
      // 2. 从 Global 获取
      return this.getDataValue(key);
    };

    // 1. 处理 hz-if (如果隐藏则停止处理)
    if (!this.ifDirective.processElement(element, getData, scopeData)) {
      return;
    }

    // 2. 处理模板文本和属性
    this.templateProcessor.processTextNodes(element, getData);

    // 3. 绑定点击事件 (使用当前的 getData闭包，包含 Access Scope)
    this.clickDirective.bind(element, this, getData);

    // 4. 递归处理子节点
    const children = Array.from(element.children) as Element[];
    for (const child of children) {
      // 如果子节点是 hz-for 生成的根节点，跳过 (因为 step 1 已经处理过它)
      if (this.forDirective.isGenerated(child)) {
        continue;
      }
      this.processElement(child, scopeData);
    }
  }

  /**
   * 从嵌套数据对象中获取值
   */
  private getDataValue(key: string): DataValue | undefined {
    // 支持 this.xxx 访问全局数据
    if (key.startsWith("this.")) {
      return this.expressionParser.getNestedValue(this.data, key.slice(5));
    }
    return this.expressionParser.getNestedValue(this.data, key);
  }

  /**
   * 清理：解除事件绑定，清空缓存
   */
  destroy(): void {
    this.clickDirective.unbindAll();
    this.templateProcessor.clear();
    this.ifDirective.clear();
    this.forDirective.clear();
  }
}
